import {
  Box,
  VStack,
  Heading,
  HStack,
  Button,
  IconButton,
  useToast,
  Icon,
  Badge,
  Text,
  Divider,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  Checkbox,
  SimpleGrid,
  Image,
  Card,
  CardBody,
  Collapse,
  Tooltip,
} from '@chakra-ui/react'
import { useNavigate } from 'react-router-dom'
import { MdArrowBack, MdRefresh, MdAdd, MdCheckCircle, MdImage } from 'react-icons/md'
import { FaMicroscope } from 'react-icons/fa'
import { useState, useMemo, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import MicroscopeImageUpload from './MicroscopeImageUpload'
import MicroscopeEvidenceGrid from './MicroscopeEvidenceGrid'
import SlideIdentifierInput from './SlideIdentifierInput'
import ParasitologyExportButton from './ParasitologyExportButton'
import CasesList from '../SpecialtyWorkspace/CasesList'
import { useDirectLabMalariaAnalysis, useUpdateMalariaBoxes } from '../../../api/api_hooks/lab/directMalaria'
import { MalariaAnalysisCase } from '../../../api/api_hooks/lab/malariaAnalyses'
import { IAnalysisMalaria } from '../../../types/ia-analysis'
import { useAIChat } from '../../../contexts/AIChatContext'
import { getSavedAuthData } from '../../../api/axios/interceptor'
import { useConsumeCredits, useCheckCredits } from '../../../api/api_hooks/credits'
import { useCHUFeatures, useCHUConfig } from '../../../contexts/CHUConfigContext'
import { isAIAccessible } from '../../../config/chuConfig'
import { useMyProfileWithModules } from '../../../api/api_hooks/user/myProfile'
import { PlanType } from '../../../types/subscription'
import { PremiumBadge } from '../../PremiumFeature'
import { LuSparkles, LuArrowRight, LuActivity, LuScanLine } from 'react-icons/lu'

interface AnalysisResult {
  infectedRbcCount: number
  uninfectedRbcCount: number
  totalCells: number
  parasitemiaPercent: number
}

interface AnalysisTiming {
  perImage: number[]       // ms per image
  totalMs: number          // total elapsed ms
  analyzedCount: number    // images analyzed so far
  totalCount: number       // total images to analyze
}

type ViewMode = 'recent-cases' | 'new-analysis' | 'case-details'

interface ParasitologyWorkspaceProps {
  caseId?: string
}

export default function ParasitologyWorkspace({ caseId }: ParasitologyWorkspaceProps) {
  const navigate = useNavigate()
  const toast = useToast()
  const { t } = useTranslation()
  const { setContext } = useAIChat()
  const apiDomain = useMemo(() => {
    return process.env.REACT_APP_API_DOMAIN || ''
  }, [])

  const { mutateAsync: consumeCredits } = useConsumeCredits()
  const { mutateAsync: checkCredits } = useCheckCredits()
  const chuConfig = useCHUConfig()
  const chuFeatures = useCHUFeatures()
  const { data: currentUser } = useMyProfileWithModules()
  const userPlan = currentUser?.plan
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'super_admin'
  // AI accessible only on Premium or Enterprise plan (or admin)
  const aiAccessible = isAdmin || userPlan === PlanType.PREMIUM || userPlan === PlanType.ENTERPRISE
  const isPremiumLocked = !aiAccessible
  // 2 crédits pour Premium (détection+classification), 1 pour Essentiel (YOLO seul)
  const creditCostPerImage = (userPlan === PlanType.PREMIUM || isAdmin) ? 2 : 1
  const [showFeatureCards, setShowFeatureCards] = useState(false)

  // View state
  const [viewMode, setViewMode] = useState<ViewMode>('recent-cases')

  // Upload state
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([])
  const [uploadedImageUrls, setUploadedImageUrls] = useState<string[]>([])
  const [newlyAddedFiles, setNewlyAddedFiles] = useState<File[]>([])
  const [newlyAddedImageUrls, setNewlyAddedImageUrls] = useState<string[]>([])

  // Analysis state
  const [analyzing, setAnalyzing] = useState(false)
  const [hasResults, setHasResults] = useState(false)
  const [analysisResults, setAnalysisResults] = useState<AnalysisResult | null>(null)
  const [individualResults, setIndividualResults] = useState<IAnalysisMalaria[]>([])

  // Index of the image currently being analyzed (-1 = none)
  const [analyzingImageIndex, setAnalyzingImageIndex] = useState(-1)

  // Debug state
  const [debugMode, setDebugMode] = useState(false)
  const [debugImages, setDebugImages] = useState<Record<string, string> | null>(null)
  const { isOpen: isDebugModalOpen, onOpen: onDebugModalOpen, onClose: onDebugModalClose } = useDisclosure()

  // Slide state
  const [slideId, setSlideId] = useState<string | null>(null)

  // Timing state (persisted in localStorage per slideId)
  const [analysisTiming, setAnalysisTimingRaw] = useState<AnalysisTiming | null>(null)
  const setAnalysisTiming = useCallback((timing: AnalysisTiming | null) => {
    setAnalysisTimingRaw(timing)
    if (timing && slideId) {
      try {
        localStorage.setItem(`parasitology-timing-${slideId}`, JSON.stringify(timing))
      } catch (_e) { /* ignore storage errors */ }
    }
  }, [slideId])

  // Case viewing state
  const [selectedCaseId, setSelectedCaseId] = useState<string | null>(null)
  const [showOnlyInfected, setShowOnlyInfected] = useState(false)
  const [showAddImagesForm, setShowAddImagesForm] = useState(false)

  // Reanalysis selection modal state
  const { isOpen: isReanalysisModalOpen, onOpen: onReanalysisModalOpen, onClose: onReanalysisModalClose } = useDisclosure()
  const [selectedImageIndices, setSelectedImageIndices] = useState<number[]>([])

  // Helper functions for image selection
  const toggleImageSelection = (index: number) => {
    setSelectedImageIndices(prev =>
      prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
    )
  }

  const selectAllImages = () => {
    setSelectedImageIndices(Array.from({ length: uploadedImageUrls.length }, (_, i) => i))
  }

  const deselectAllImages = () => {
    setSelectedImageIndices([])
  }

  const { mutateAsync: analyzeImage } = useDirectLabMalariaAnalysis()
  const { mutateAsync: updateBoxes } = useUpdateMalariaBoxes()

  // Après une analyse fraîche pour Essentiel, persister l'état neutralisé en DB
  // pour que le rechargement retrouve "toutes les boxes en non classifié"
  const persistNeutralizedResult = useCallback(async (result: IAnalysisMalaria) => {
    if (aiAccessible || !result.id) return
    try {
      await updateBoxes({
        imageId: result.id,
        positiveBoxes: [],
        negativeBoxes: [...(result.negativeBoxes ?? [])],
      })
    } catch (_e) { /* non bloquant */ }
  }, [aiAccessible, updateBoxes])

  // Essentiel: merge CNN-positive boxes into negative → user labels manually from 0
  const neutralizeForManualLabeling = useCallback((result: IAnalysisMalaria): IAnalysisMalaria => {
    if (aiAccessible) return result
    const allBoxes = [...(result.positiveBoxes ?? []), ...(result.negativeBoxes ?? [])]
    return {
      ...result,
      positiveBoxes: [],
      negativeBoxes: allBoxes,
      infectedRbcCount: 0,
      uninfectedRbcCount: allBoxes.length,
      estimatedParasitemiaPercent: 0,
      parasitemiaPercent: 0,
    }
  }, [aiAccessible])

  // Helper: recompute aggregated results from individual results
  const computeAggregated = useCallback((results: IAnalysisMalaria[]): AnalysisResult => {
    const agg = results.reduce(
      (acc, r) => {
        acc.infectedRbcCount += r.infectedRbcCount
        acc.uninfectedRbcCount += r.uninfectedRbcCount
        return acc
      },
      { infectedRbcCount: 0, uninfectedRbcCount: 0, totalCells: 0, parasitemiaPercent: 0 }
    )
    agg.totalCells = agg.infectedRbcCount + agg.uninfectedRbcCount
    agg.parasitemiaPercent = agg.totalCells > 0 ? (agg.infectedRbcCount / agg.totalCells) * 100 : 0
    return agg
  }, [])

  const normalizeImageUrl = useCallback((url?: string | null) => {
    if (!url) return ''
    let trimmed = url.trim()
    if (
      trimmed.startsWith('http://') ||
      trimmed.startsWith('https://') ||
      trimmed.startsWith('blob:') ||
      trimmed.startsWith('data:')
    ) {
      return trimmed
    }

    trimmed = trimmed.replace(/\\/g, '/')

    if (trimmed.startsWith('public/uploads')) {
      trimmed = trimmed.replace(/^public\/uploads/, '/uploads')
    } else if (trimmed.startsWith('/public/uploads')) {
      trimmed = trimmed.replace(/^\/public\/uploads/, '/uploads')
    }

    if (trimmed.startsWith('uploads/')) {
      trimmed = `/uploads/${trimmed.slice('uploads/'.length)}`
    }

    const normalized = trimmed.startsWith('/') ? trimmed : `/${trimmed}`
    return `${apiDomain.replace(/\/$/, '')}${normalized}`
  }, [apiDomain])

  // Handler pour upload d'images (SANS analyse automatique)
  const handleImagesUploaded = async (files: File[], imageUrls: string[]) => {
    setUploadedFiles(files)
    setUploadedImageUrls(imageUrls)

    toast({
      title: 'Images uploaded successfully',
      description: `${files.length} image(s) ready for analysis`,
      status: 'success',
      duration: 2000,
    })
  }

  // Handler pour ajouter de nouvelles images à un cas existant
  const handleNewImagesAdded = async (files: File[], imageUrls: string[]) => {
    setNewlyAddedFiles(files)
    setNewlyAddedImageUrls(imageUrls)

    toast({
      title: 'New images added',
      description: `${files.length} new image(s) ready for analysis`,
      status: 'success',
      duration: 2000,
    })
  }

  // Handler pour analyser UNIQUEMENT les nouvelles images ajoutées
  const handleAnalyzeNewOnly = useCallback(async () => {
    if (newlyAddedFiles.length === 0) {
      toast({ title: 'No new images to analyze', status: 'warning', duration: 2000 })
      return
    }

    try {
      const creditCheck = await checkCredits({ amount: creditCostPerImage * newlyAddedFiles.length })
      if (!creditCheck.hasEnoughCredits) {
        toast({
          title: t('credits.insufficient'),
          description: t('credits.insufficientDescription', { remaining: creditCheck.remainingCredits }),
          status: 'error', duration: 5000, isClosable: true,
        })
        return
      }
    } catch (_e) {}

    setAnalyzing(true)
    setAnalysisTiming({ perImage: [], totalMs: 0, analyzedCount: 0, totalCount: newlyAddedFiles.length })

    const totalStart = Date.now()

    try {
      toast({
        title: 'Starting AI analysis...',
        description: `Analyzing ${newlyAddedFiles.length} new blood smear image(s)`,
        status: 'info',
        duration: 2000,
      })

      const newResults: IAnalysisMalaria[] = []
      const perImageTimes: number[] = []

      for (let i = 0; i < newlyAddedFiles.length; i++) {
        const imgStart = Date.now()
        try {
          const result = await analyzeImage({
            file: newlyAddedFiles[i],
            slideId: slideId!,
            debugImages: debugMode
          })
          const imgTime = Date.now() - imgStart
          perImageTimes.push(imgTime)

          const neutralized = neutralizeForManualLabeling(result.analysis); newResults.push(neutralized); void persistNeutralizedResult(neutralized)

          if (debugMode && result.debugImages) {
            setDebugImages(result.debugImages)
          }

          // Update progressively
          const combinedSoFar = [...individualResults, ...newResults]
          setIndividualResults(combinedSoFar)
          setUploadedImageUrls([...uploadedImageUrls, ...newlyAddedImageUrls.slice(0, newResults.length)])
          setAnalysisResults(computeAggregated(combinedSoFar))
          setHasResults(true)
          setAnalysisTiming({
            perImage: [...perImageTimes],
            totalMs: Date.now() - totalStart,
            analyzedCount: newResults.length,
            totalCount: newlyAddedFiles.length,
          })

          toast({
            title: `Image ${i + 1}/${newlyAddedFiles.length} analyzed (${(imgTime / 1000).toFixed(1)}s)`,
            status: 'info',
            duration: 1500,
          })
        } catch (error) {
          perImageTimes.push(Date.now() - imgStart)
          console.error(`Error analyzing new image ${i + 1}:`, error)
          toast({
            title: `Error analyzing image ${i + 1}`,
            description: 'Skipping this image',
            status: 'warning',
            duration: 2000,
          })
        }
      }

      const totalMs = Date.now() - totalStart

      // Final state
      const combinedResults = [...individualResults, ...newResults]
      setIndividualResults(combinedResults)
      setUploadedImageUrls([...uploadedImageUrls, ...newlyAddedImageUrls])
      const aggregated = computeAggregated(combinedResults)
      setAnalysisResults(aggregated)
      setHasResults(true)
      setAnalysisTiming({ perImage: perImageTimes, totalMs, analyzedCount: newResults.length, totalCount: newlyAddedFiles.length })

      setViewMode('case-details')
      setSelectedCaseId(slideId!)
      navigate(`/lab/parasitology/${slideId}`, { replace: true })

      setNewlyAddedFiles([])
      setNewlyAddedImageUrls([])
      setShowAddImagesForm(false)

      toast({
        title: `New Images Analyzed (${(totalMs / 1000).toFixed(1)}s)`,
        duration: 4000,
        isClosable: true,
      })

      // Consommer les crédits après succès
      consumeCredits({
        amount: creditCostPerImage * newResults.length,
        action: 'malaria_analysis',
        // resourceId: slideId — not UUID, skipped
        resourceType: 'parasitology_case',
        metadata: { imageCount: newResults.length },
      }).catch((err) => console.error("[Credits] consume failed:", err))
    } catch (error: any) {
      toast({
        title: 'Analysis Failed',
        description: error.message || 'Failed to analyze new images',
        status: 'error', duration: 5000, isClosable: true,
      })
    } finally {
      setAnalyzing(false)
    }
  }, [newlyAddedFiles, newlyAddedImageUrls, individualResults, uploadedImageUrls, slideId, analyzeImage, toast, navigate, debugMode, computeAggregated, setAnalysisTiming, checkCredits, consumeCredits, creditCostPerImage, t])

  // Open reanalysis modal with image selection
  const handleOpenReanalysisModal = useCallback(() => {
    const totalImages = uploadedImageUrls.length
    if (totalImages === 0) {
      toast({
        title: 'No images to analyze',
        description: 'No images found for this case',
        status: 'warning',
        duration: 2000,
      })
      return
    }

    // Select all images by default
    setSelectedImageIndices(Array.from({ length: totalImages }, (_, i) => i))
    onReanalysisModalOpen()
  }, [uploadedImageUrls.length, toast, onReanalysisModalOpen])

  // Perform reanalysis on selected images
  const performReanalysis = useCallback(async () => {
    onReanalysisModalClose()

    if (selectedImageIndices.length === 0) {
      toast({
        title: 'No images selected',
        description: 'Please select at least one image to reanalyze',
        status: 'warning',
        duration: 2000,
      })
      return
    }

    try {
      const creditCheck = await checkCredits({ amount: creditCostPerImage * selectedImageIndices.length })
      if (!creditCheck.hasEnoughCredits) {
        toast({
          title: t('credits.insufficient'),
          description: t('credits.insufficientDescription', { remaining: creditCheck.remainingCredits }),
          status: 'error', duration: 5000, isClosable: true,
        })
        return
      }
    } catch (_e) {}

    setAnalyzing(true)
    setHasResults(false)
    setIndividualResults([])
    setAnalysisTiming({ perImage: [], totalMs: 0, analyzedCount: 0, totalCount: selectedImageIndices.length })

    const totalStart = Date.now()

    try {
      toast({
        title: 'Starting AI reanalysis...',
        description: `Reanalyzing ${selectedImageIndices.length} blood smear image(s)`,
        status: 'info',
        duration: 2000,
      })

      const results: IAnalysisMalaria[] = []
      const perImageTimes: number[] = []

      for (let i = 0; i < selectedImageIndices.length; i++) {
        const index = selectedImageIndices[i]
        setAnalyzingImageIndex(index)
        const existingImageUrl = uploadedImageUrls[index]
        const imgStart = Date.now()

        try {
          const result = await analyzeImage({
            slideId: slideId!,
            existingImageUrl,
            debugImages: debugMode
          })
          const imgTime = Date.now() - imgStart
          perImageTimes.push(imgTime)

          const neutralized = neutralizeForManualLabeling(result.analysis); results.push(neutralized); void persistNeutralizedResult(neutralized)

          if (debugMode && result.debugImages) {
            setDebugImages(result.debugImages)
          }

          // Update progressively
          setIndividualResults([...results])
          setAnalysisResults(computeAggregated(results))
          setHasResults(true)
          setAnalysisTiming({
            perImage: [...perImageTimes],
            totalMs: Date.now() - totalStart,
            analyzedCount: results.length,
            totalCount: selectedImageIndices.length,
          })

          toast({
            title: `Image ${i + 1}/${selectedImageIndices.length} analyzed (${(imgTime / 1000).toFixed(1)}s)`,
            status: 'info',
            duration: 1500,
          })
        } catch (error) {
          perImageTimes.push(Date.now() - imgStart)
          console.error(`Error analyzing image ${index + 1}:`, error)
          toast({
            title: `Error analyzing image ${index + 1}`,
            description: 'Skipping this image',
            status: 'warning',
            duration: 2000,
          })
        }
      }

      const totalMs = Date.now() - totalStart
      const aggregated = computeAggregated(results)
      setAnalysisTiming({ perImage: perImageTimes, totalMs, analyzedCount: results.length, totalCount: selectedImageIndices.length })

      setViewMode('case-details')
      setSelectedCaseId(slideId!)
      navigate(`/lab/parasitology/${slideId}`, { replace: true })

      toast({
        title: `Ré-analyse terminée (${(totalMs / 1000).toFixed(1)}s)`,
        status: aggregated.parasitemiaPercent > 5 ? 'warning' : 'success',
        duration: 3000,
        isClosable: true,
      })

      consumeCredits({
        amount: creditCostPerImage * results.length,
        action: 'malaria_analysis',
        // resourceId: slideId — not UUID, skipped
        resourceType: 'parasitology_case',
        metadata: { imageCount: results.length },
      }).catch((err) => console.error("[Credits] consume failed:", err))
    } catch (error: any) {
      console.error('Error during reanalysis:', error)
      toast({
        title: 'Reanalysis Failed',
        description: error.message || 'Failed to reanalyze images',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    } finally {
      setAnalyzing(false)
      setAnalyzingImageIndex(-1)
    }
  }, [selectedImageIndices, uploadedImageUrls, slideId, analyzeImage, toast, onReanalysisModalClose, navigate, debugMode, computeAggregated, setAnalysisTiming, checkCredits, consumeCredits, creditCostPerImage, t])

  // Handler pour relancer l'analyse UNIQUEMENT des images existantes (sans nouvelles images)
  const handleRerunExistingOnly = useCallback(async () => {
    handleOpenReanalysisModal()
  }, [handleOpenReanalysisModal])

  // Handler pour relancer l'analyse de TOUTES les images (anciennes + nouvelles)
  const handleRerunAllAnalysis = useCallback(async () => {
    // Helper pour télécharger une image depuis une URL et la convertir en File
    const downloadImageAsFile = async (url: string, index: number): Promise<File> => {
      const response = await fetch(url)
      const blob = await response.blob()
      const filename = `slide-${slideId}-image-${index + 1}.jpg`
      return new File([blob], filename, { type: blob.type })
    }

    if (uploadedImageUrls.length === 0 && newlyAddedFiles.length === 0) {
      toast({
        title: 'No images to analyze',
        description: 'No images found',
        status: 'warning',
        duration: 2000,
      })
      return
    }

    const totalImages = uploadedImageUrls.length + newlyAddedFiles.length
    try {
      const creditCheck = await checkCredits({ amount: creditCostPerImage * totalImages })
      if (!creditCheck.hasEnoughCredits) {
        toast({
          title: t('credits.insufficient'),
          description: t('credits.insufficientDescription', { remaining: creditCheck.remainingCredits }),
          status: 'error', duration: 5000, isClosable: true,
        })
        return
      }
    } catch (_e) {}

    setAnalyzing(true)
    setHasResults(false)
    setIndividualResults([])

    try {
      toast({
        title: 'Téléchargement des images...',
        description: `Préparation de ${uploadedImageUrls.length} image(s) pour ré-analyse`,
        status: 'info',
        duration: 2000,
      })

      const existingFiles: File[] = []
      for (let i = 0; i < uploadedImageUrls.length; i++) {
        try {
          const file = await downloadImageAsFile(uploadedImageUrls[i], i)
          existingFiles.push(file)
        } catch (error) {
          console.error(`Error downloading image ${i + 1}:`, error)
          toast({
            title: `Error downloading image ${i + 1}`,
            description: 'Skipping this image',
            status: 'warning',
            duration: 2000,
          })
        }
      }

      const allFiles = [...existingFiles, ...newlyAddedFiles]
      const allImageUrls = [...uploadedImageUrls, ...newlyAddedImageUrls]

      if (allFiles.length === 0) {
        throw new Error('Failed to prepare any images')
      }

      setUploadedFiles(allFiles)
      setAnalysisTiming({ perImage: [], totalMs: 0, analyzedCount: 0, totalCount: allFiles.length })

      const totalStart = Date.now()

      toast({
        title: 'Starting AI reanalysis...',
        description: `Analyzing ${allFiles.length} blood smear image(s)`,
        status: 'info',
        duration: 2000,
      })

      const results: IAnalysisMalaria[] = []
      const perImageTimes: number[] = []

      for (let i = 0; i < allFiles.length; i++) {
        setAnalyzingImageIndex(i)
        const imgStart = Date.now()
        try {
          const result = await analyzeImage({
            file: allFiles[i],
            slideId: slideId!,
            debugImages: debugMode
          })
          const imgTime = Date.now() - imgStart
          perImageTimes.push(imgTime)

          const neutralized = neutralizeForManualLabeling(result.analysis); results.push(neutralized); void persistNeutralizedResult(neutralized)

          if (debugMode && result.debugImages) {
            setDebugImages(result.debugImages)
          }

          // Update progressively
          setIndividualResults([...results])
          setUploadedImageUrls(allImageUrls.slice(0, results.length))
          setAnalysisResults(computeAggregated(results))
          setHasResults(true)
          setAnalysisTiming({
            perImage: [...perImageTimes],
            totalMs: Date.now() - totalStart,
            analyzedCount: results.length,
            totalCount: allFiles.length,
          })

          toast({
            title: `Image ${i + 1}/${allFiles.length} analyzed (${(imgTime / 1000).toFixed(1)}s)`,
            status: 'info',
            duration: 1500,
          })
        } catch (error) {
          perImageTimes.push(Date.now() - imgStart)
          console.error(`Error analyzing image ${i + 1}:`, error)
          toast({
            title: `Error analyzing image ${i + 1}`,
            description: 'Skipping this image',
            status: 'warning',
            duration: 2000,
          })
        }
      }

      const totalMs = Date.now() - totalStart
      setUploadedImageUrls(allImageUrls)
      const aggregated = computeAggregated(results)
      setAnalysisTiming({ perImage: perImageTimes, totalMs, analyzedCount: results.length, totalCount: allFiles.length })

      setViewMode('case-details')
      setSelectedCaseId(slideId!)
      navigate(`/lab/parasitology/${slideId}`, { replace: true })

      setNewlyAddedFiles([])
      setNewlyAddedImageUrls([])
      setShowAddImagesForm(false)

      toast({
        title: `Ré-analyse terminée (${(totalMs / 1000).toFixed(1)}s)`,
        status: aggregated.parasitemiaPercent > 5 ? 'warning' : 'success',
        duration: 3000,
        isClosable: true,
      })

      consumeCredits({
        amount: creditCostPerImage * results.length,
        action: 'malaria_analysis',
        // resourceId: slideId — not UUID, skipped
        resourceType: 'parasitology_case',
        metadata: { imageCount: results.length },
      }).catch((err) => console.error("[Credits] consume failed:", err))
    } catch (error: any) {
      console.error('Error during reanalysis:', error)
      toast({
        title: 'Reanalysis Failed',
        description: error.message || 'Failed to reanalyze images',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    } finally {
      setAnalyzing(false)
      setAnalyzingImageIndex(-1)
    }
  }, [uploadedImageUrls, newlyAddedFiles, newlyAddedImageUrls, slideId, analyzeImage, toast, navigate, debugMode, computeAggregated, setAnalysisTiming, checkCredits, consumeCredits, creditCostPerImage, t])

  // Handler pour lancer l'analyse MANUELLEMENT
  const handleRunAnalysis = useCallback(async () => {
    if (uploadedFiles.length === 0) {
      toast({
        title: 'No images to analyze',
        description: 'Please upload images first',
        status: 'warning',
        duration: 2000,
      })
      return
    }

    // Vérifier les crédits avant de lancer l'analyse
    try {
      const creditCheck = await checkCredits({ amount: creditCostPerImage })
      if (!creditCheck.hasEnoughCredits) {
        toast({
          title: t('credits.insufficient'),
          description: t('credits.insufficientDescription', { remaining: creditCheck.remainingCredits }),
          status: 'error',
          duration: 5000,
          isClosable: true,
        })
        return
      }
    } catch (_e) {
      // Si le check échoue on laisse l'analyse continuer (fail open)
    }

    setAnalyzing(true)
    setHasResults(false)
    setIndividualResults([])
    setAnalysisTiming({ perImage: [], totalMs: 0, analyzedCount: 0, totalCount: uploadedFiles.length })

    const totalStart = Date.now()
    // Keep local blob URLs so images stay visible during analysis
    const currentUrls = [...uploadedImageUrls]

    try {
      const results: IAnalysisMalaria[] = []
      const perImageTimes: number[] = []

      // Switch to case-details view immediately so results appear progressively
      setViewMode('case-details')
      setSelectedCaseId(slideId!)
      navigate(`/lab/parasitology/${slideId}`, { replace: true })

      for (let i = 0; i < uploadedFiles.length; i++) {
        setAnalyzingImageIndex(i)
        const imgStart = Date.now()
        try {
          const result = await analyzeImage({ file: uploadedFiles[i], slideId: slideId! })
          const imgTime = Date.now() - imgStart
          perImageTimes.push(imgTime)

          const neutralized = neutralizeForManualLabeling(result.analysis); results.push(neutralized); void persistNeutralizedResult(neutralized)
          // Replace the local blob URL with the server URL for this image
          currentUrls[i] = normalizeImageUrl(result.imageUrl)

          // Update state progressively after each image
          setIndividualResults([...results])
          setUploadedImageUrls([...currentUrls])
          setAnalysisResults(computeAggregated(results))
          setHasResults(true)
          setAnalysisTiming({
            perImage: [...perImageTimes],
            totalMs: Date.now() - totalStart,
            analyzedCount: results.length,
            totalCount: uploadedFiles.length,
          })
        } catch (error) {
          const imgTime = Date.now() - imgStart
          perImageTimes.push(imgTime)
          console.error(`Error analyzing image ${i + 1}:`, error)
          toast({
            title: `Error analyzing image ${i + 1}`,
            description: 'Skipping this image',
            status: 'warning',
            duration: 2000,
          })
        }
      }

      setAnalyzingImageIndex(-1)
      const totalMs = Date.now() - totalStart
      setAnalysisTiming({
        perImage: perImageTimes,
        totalMs,
        analyzedCount: results.length,
        totalCount: uploadedFiles.length,
      })

      const aggregated = computeAggregated(results)

      // Consommer crédits après analyse réussie (1 Essentiel, 2 Premium)
      consumeCredits({
        amount: creditCostPerImage,
        action: 'malaria_analysis',
        // resourceId: slideId — not UUID, skipped
        resourceType: 'parasitology_case',
        metadata: { imageCount: results.length },
      }).catch((err) => console.error('[Credits] consume failed:', err))

      toast({
        title: `Analysis Complete (${(totalMs / 1000).toFixed(1)}s)`,
        description: `Detected ${aggregated.infectedRbcCount} infected cells across ${results.length} image(s)`,
        status: aggregated.parasitemiaPercent > 5 ? 'warning' : 'success',
        duration: 4000,
        isClosable: true,
      })
    } catch (error: any) {
      console.error('Error during analysis:', error)
      toast({
        title: 'Analysis Failed',
        description: error.message || 'Failed to analyze images',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    } finally {
      setAnalyzing(false)
      setAnalyzingImageIndex(-1)
    }
  }, [uploadedFiles, uploadedImageUrls, slideId, analyzeImage, toast, navigate, normalizeImageUrl, computeAggregated, setAnalysisTiming, checkCredits, consumeCredits, creditCostPerImage, t])

  const handleSlideIdConfirmed = (id: string) => {
    setSlideId(id)
  }

  const handleNewAnalysis = useCallback(() => {
    setViewMode('new-analysis')
    setUploadedFiles([])
    setUploadedImageUrls([])
    setNewlyAddedFiles([])
    setNewlyAddedImageUrls([])
    setShowAddImagesForm(false)
    setHasResults(false)
    setAnalyzing(false)
    setAnalysisResults(null)
    setIndividualResults([])
    setAnalysisTiming(null)
    setSlideId(null)
    setShowOnlyInfected(false)
    setSelectedCaseId(null)
    // Clear case ID from URL
    navigate('/lab/parasitology', { replace: true })
  }, [navigate, setAnalysisTiming])

  const handleBackToRecentCases = useCallback(() => {
    setViewMode('recent-cases')
    setSelectedCaseId(null)
    setNewlyAddedFiles([])
    setNewlyAddedImageUrls([])
    setShowAddImagesForm(false)
    // Navigate to recent cases route
    navigate('/lab/parasitology', { replace: true })
  }, [navigate])

  // Smart back navigation based on current view
  const handleSmartBack = useCallback(() => {
    if (viewMode === 'recent-cases') {
      // From recent cases, go back to lab store
      navigate('/lab')
    } else {
      // From new-analysis or case-details, go back to recent cases
      handleBackToRecentCases()
    }
  }, [viewMode, navigate, handleBackToRecentCases])

  // Handler pour changer le label d'une box et mettre à jour les statistiques
  const handleBoxLabelChange = useCallback(
    (
      imageIndex: number,
      newPositiveCount: number,
      newNegativeCount: number,
      newPositiveBoxes: number[][],
      newNegativeBoxes: number[][]
    ) => {
      // Mettre à jour les résultats individuels pour cette image avec les nouvelles boxes
      setIndividualResults((prevResults) => {
        const updatedResults = [...prevResults]
        if (updatedResults[imageIndex]) {
          updatedResults[imageIndex] = {
            ...updatedResults[imageIndex],
            infectedRbcCount: newPositiveCount,
            uninfectedRbcCount: newNegativeCount,
            positiveBoxes: newPositiveBoxes,
            negativeBoxes: newNegativeBoxes,
          }
        }

        // Recalculer immédiatement les statistiques agrégées avec les résultats mis à jour
        const newInfectedTotal = updatedResults.reduce((sum, result) => sum + result.infectedRbcCount, 0)
        const newUninfectedTotal = updatedResults.reduce((sum, result) => sum + result.uninfectedRbcCount, 0)
        const totalCells = newInfectedTotal + newUninfectedTotal
        const parasitemiaPercent = totalCells > 0 ? (newInfectedTotal / totalCells) * 100 : 0

        setAnalysisResults({
          infectedRbcCount: newInfectedTotal,
          uninfectedRbcCount: newUninfectedTotal,
          totalCells,
          parasitemiaPercent,
        })

        return updatedResults
      })

      toast({
        title: 'Label modifié',
        description: 'Les statistiques ont été mises à jour et sauvegardées',
        status: 'success',
        duration: 1500,
      })
    },
    [toast]
  )

  const handleRecentCaseSelect = useCallback((caseItem: MalariaAnalysisCase) => {
    setViewMode('case-details')
    setSelectedCaseId(caseItem.id)
    setUploadedFiles([])
    setNewlyAddedFiles([])
    setNewlyAddedImageUrls([])
    setShowAddImagesForm(false)
    setAnalyzing(false)
    setHasResults(true)

    // Update URL to include case ID
    navigate(`/lab/parasitology/${caseItem.id}`, { replace: true })

    const totalCells = caseItem.infectedRbcCount + caseItem.uninfectedRbcCount
    const parasitemia =
      caseItem.parasitemiaPercent ??
      (totalCells > 0 ? (caseItem.infectedRbcCount / totalCells) * 100 : 0)

    setSlideId(caseItem.slideId)
    setUploadedImageUrls(
      caseItem.images
        .map((image) => normalizeImageUrl(image.imageUrl))
        .filter((url) => Boolean(url))
    )
    setAnalysisResults({
      infectedRbcCount: caseItem.infectedRbcCount,
      uninfectedRbcCount: caseItem.uninfectedRbcCount,
      totalCells,
      parasitemiaPercent: parasitemia,
    })
    setIndividualResults(
      caseItem.images.map((image) => ({
        id: image.id,
        createdAt: image.createdAt,
        updatedAt: image.createdAt,
        infectedRbcCount: image.infectedRbcCount,
        uninfectedRbcCount: image.uninfectedRbcCount,
        estimatedParasitemiaPercent: image.parasitemiaPercent,
        parasitemiaPercent: image.parasitemiaPercent,
        positiveBoxes: image.positiveBoxes ?? [],
        negativeBoxes: image.negativeBoxes ?? [],
        detectionProfile: image.detectionProfile ?? null,
      }))
    )
    setShowOnlyInfected(false)

    // Restore timing from localStorage if available
    try {
      const savedTiming = localStorage.getItem(`parasitology-timing-${caseItem.slideId}`)
      if (savedTiming) {
        setAnalysisTimingRaw(JSON.parse(savedTiming))
      } else {
        setAnalysisTimingRaw(null)
      }
    } catch (_e) {
      setAnalysisTimingRaw(null)
    }

    toast({
      title: 'Case loaded',
      description: `Slide ID: ${caseItem.slideId} • ${caseItem.imageCount} images`,
      status: 'info',
      duration: 3000,
    })
  }, [navigate, toast, normalizeImageUrl])

  // Load case automatically if caseId is provided in URL
  useEffect(() => {
    if (!caseId) return
    // Only skip if the case is loaded AND we're not in the middle of analyzing
    if (selectedCaseId === caseId && hasResults && !analyzing) return

    const loadCaseFromUrl = async () => {
      try {
        console.log('🔗 Loading malaria case from URL:', caseId)
        const { accessToken } = getSavedAuthData()

        const apiUrl = `${apiDomain}/api/lab-analysis/malaria/case/${caseId}`
        console.log('📡 API URL:', apiUrl)

        const response = await fetch(apiUrl, {
          headers: {
            'Authorization': accessToken || '',
            'Content-Type': 'application/json',
          },
        })

        console.log('📥 Response status:', response.status, response.statusText)

        if (!response.ok) {
          const errorBody = await response.text()
          console.error('❌ Error response body:', errorBody)
          throw new Error(`Failed to load case: ${response.statusText} - ${errorBody}`)
        }

        const data = await response.json()

        // Transform the data to match MalariaAnalysisCase interface
        const caseData: MalariaAnalysisCase = {
          id: data.diagnosticImageId,
          slideId: data.analysis?.slideId || '',
          date: data.analysis?.createdAt || new Date().toISOString(),
          imageCount: data.analysis?.images?.length || 0,
          status: data.analysis?.status || 'completed',
          summary: data.analysis?.summary || '',
          infectedRbcCount: data.analysis?.infectedRbcCount || 0,
          uninfectedRbcCount: data.analysis?.uninfectedRbcCount || 0,
          parasitemiaPercent: data.analysis?.parasitemiaPercent || 0,
          images: (data.analysis?.images || []).map((img: any) => ({
            id: img.id,
            imageUrl: img.imageUrl,
            infectedRbcCount: img.infectedRbcCount || 0,
            uninfectedRbcCount: img.uninfectedRbcCount || 0,
            parasitemiaPercent: img.parasitemiaPercent || 0,
            positiveBoxes: img.positiveBoxes || [],
            negativeBoxes: img.negativeBoxes || [],
            createdAt: img.createdAt || new Date().toISOString(),
            detectionProfile: img.detectionProfile ?? null,
          })),
        }

        // Load the case using existing handler
        handleRecentCaseSelect(caseData)
      } catch (error) {
        console.error('❌ Error loading case from URL:', error)
        toast({
          title: 'Erreur',
          description: 'Impossible de charger le cas',
          status: 'error',
          duration: 5000,
        })
        // Navigate back to recent cases on error
        navigate('/lab/parasitology', { replace: true })
      }
    }

    loadCaseFromUrl()
  }, [caseId, apiDomain, toast, navigate, handleRecentCaseSelect, selectedCaseId, hasResults])

  // Définir le contexte ALMA pour l'agent IA
  useEffect(() => {
    const allPositiveBoxes = individualResults.flatMap((result) => result.positiveBoxes || [])
    const allNegativeBoxes = individualResults.flatMap((result) => result.negativeBoxes || [])

    setContext({
      type: 'alma',
      slideId: slideId || undefined,
      analysisResults: analysisResults
        ? {
            ...analysisResults,
            positiveBoxes: allPositiveBoxes,
            negativeBoxes: allNegativeBoxes,
          }
        : undefined,
      availableActions: analysisResults
        ? [
            'show_infected_cells',
            'show_annotated_images',
            'get_parasitemia',
            'count_positive_cells',
            'summarize_results',
          ]
        : [],
      // Callbacks pour les commandes de workflow
      almaWorkflowCallbacks: {
        onLaunchAnalysis: handleRunAnalysis,
        onNewAnalysis: handleNewAnalysis,
        onViewRecentCases: handleBackToRecentCases,
      },
      // État du workflow pour les suggestions contextuelles
      almaWorkflowState: {
        currentView: viewMode,
        hasUploadedImages: uploadedImageUrls.length > 0,
        imagesCount: uploadedImageUrls.length,
        isAnalyzing: analyzing,
        hasResults,
      },
    })

    return () => {
      setContext({ type: 'general' })
    }
  }, [
    analysisResults,
    individualResults,
    slideId,
    setContext,
    viewMode,
    uploadedImageUrls.length,
    analyzing,
    hasResults,
    handleRunAnalysis,
    handleNewAnalysis,
    handleBackToRecentCases,
  ])

  return (
    <VStack spacing={4} align="stretch" h="100%" w="100%" maxW="1400px" mx="auto" px={4}>
      {/* Header */}
      <Box
        bgGradient="linear(135deg, purple.600 0%, purple.500 50%, pink.400 100%)"
        borderRadius="2xl"
        p={6}
        position="relative"
        overflow="hidden"
        boxShadow="2xl"
        _before={{
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 30% 20%, rgba(255,255,255,0.2) 0%, transparent 50%)',
          pointerEvents: 'none',
        }}
      >
        <HStack justify="space-between" position="relative" zIndex={1}>
          <HStack spacing={4}>
            <IconButton
              aria-label="Go Back"
              icon={<MdArrowBack />}
              onClick={handleSmartBack}
              variant="ghost"
              color="white"
              _hover={{ bg: 'whiteAlpha.300' }}
              size="lg"
            />
            <Box
              p={3}
              bg="whiteAlpha.300"
              backdropFilter="blur(10px)"
              borderRadius="xl"
              border="1px solid"
              borderColor="whiteAlpha.400"
            >
              <Icon as={FaMicroscope} boxSize={8} color="white" />
            </Box>
            <VStack spacing={1} align="start">
              <Heading size="lg" color="white" fontWeight="bold" letterSpacing="tight">
                {t('lab.parasitology.title')}
              </Heading>
              <HStack spacing={1.5}>
                <Badge colorScheme="green" variant="solid" fontSize="2xs" px={1.5} py={0.5} borderRadius="md">
                  AI-Powered
                </Badge>
                <Badge colorScheme="orange" variant="solid" fontSize="2xs" px={1.5} py={0.5} borderRadius="md">
                  BETA
                </Badge>
              </HStack>
            </VStack>
          </HStack>

          <HStack spacing={3}>
            <Button
              leftIcon={viewMode === 'new-analysis' ? <MdRefresh /> : <MdAdd />}
              onClick={handleNewAnalysis}
              variant="solid"
              bg="whiteAlpha.300"
              color="white"
              _hover={{ bg: 'whiteAlpha.400' }}
              transition="all 0.3s"
              size="lg"
              borderRadius="xl"
            >
              {viewMode === 'new-analysis' ? 'Réinitialiser' : 'Nouvelle analyse'}
            </Button>
          </HStack>
        </HStack>
      </Box>

      {/* ── Premium gate (liste uniquement nouvelle analyse) ─────────────── */}
      {isPremiumLocked && viewMode === 'new-analysis' && (
        <Box
          position="relative"
          overflow="hidden"
          bg="linear-gradient(145deg, #faf5ff 0%, #fff 35%, #f0f9ff 100%)"
          border="1px solid"
          borderColor="purple.200"
          borderRadius="xl"
          p={{ base: 3, md: 4 }}
          boxShadow="sm"
        >
          <VStack align="stretch" spacing={3} position="relative" zIndex={1}>
            {/* Header row — compact */}
            <HStack justify="space-between" align="center" spacing={4}>
              <HStack spacing={3} align="center" flex={1}>
                <Box
                  w="36px"
                  h="36px"
                  borderRadius="lg"
                  bg="linear-gradient(135deg, #805ad5 0%, #3182ce 100%)"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  flexShrink={0}
                >
                  <Icon as={LuSparkles} color="white" boxSize={4} />
                </Box>
                <VStack align="start" spacing={0}>
                  <HStack spacing={2} mb={0.5}>
                    <Badge bg="purple.500" color="white" px={2} py={0.5} borderRadius="full" fontSize="2xs">BETA</Badge>
                    <PremiumBadge />
                  </HStack>
                  <Text fontWeight="bold" fontSize="sm" color="gray.900">
                    Détection IA des globules parasités
                  </Text>
                  <Text fontSize="xs" color="gray.500">
                    Accès anticipé · Validation clinique en cours
                  </Text>
                </VStack>
              </HStack>
              <Button
                size="md"
                bgGradient="linear(to-r, purple.500, blue.400)"
                color="white"
                rightIcon={<Icon as={LuArrowRight} />}
                borderRadius="xl"
                px={6}
                py={5}
                fontWeight="bold"
                fontSize="sm"
                flexShrink={0}
                boxShadow="0 4px 20px rgba(128,90,213,0.4)"
                _hover={{
                  bgGradient: 'linear(to-r, purple.600, blue.500)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 28px rgba(128,90,213,0.5)',
                }}
                transition="all 0.2s"
                onClick={() => navigate('/change-plan')}
              >
                Passer au Premium
              </Button>
            </HStack>

            {/* Toggle */}
            <Box
              as="button"
              onClick={() => setShowFeatureCards(v => !v)}
              w="100%"
              py={2}
              display="flex"
              alignItems="center"
              justifyContent="center"
              gap={2}
              color="purple.500"
              fontSize="xs"
              fontWeight="semibold"
              letterSpacing="0.04em"
              _hover={{ color: 'purple.700' }}
              transition="color 0.2s"
            >
              <Text>{showFeatureCards ? 'Masquer les détails' : 'Voir ce qui est inclus'}</Text>
              <Box
                transform={showFeatureCards ? 'rotate(180deg)' : 'rotate(0deg)'}
                transition="transform 0.25s ease"
                fontSize="14px"
              >
                ▾
              </Box>
            </Box>

            {/* Feature cards */}
            <Collapse in={showFeatureCards} animateOpacity>
            <SimpleGrid columns={{ base: 1, xl: 3 }} spacing={4}>
              {[
                {
                  key: 'detection',
                  icon: LuSparkles,
                  color: 'linear(to-br, purple.100, pink.100)',
                  accent: 'purple.500',
                  title: 'Détection automatique ALMA',
                  desc: 'Le modèle ALMA classe chaque cellule infectée / saine. 80% de temps gagné vs comptage manuel.',
                  stat: '80% faster',
                },
                {
                  key: 'accuracy',
                  icon: LuActivity,
                  color: 'linear(to-br, blue.100, cyan.100)',
                  accent: 'blue.500',
                  title: 'Précision clinique',
                  desc: 'Accuracy 99.7% · Sensibilité 77.9% · Spécificité 99.8% — évaluation patient-level.',
                  stat: '99.7% accuracy',
                },
                {
                  key: 'export',
                  icon: LuScanLine,
                  color: 'linear(to-br, teal.100, green.100)',
                  accent: 'teal.500',
                  title: 'Export PDF enrichi',
                  desc: 'Images annotées, overlay de détection, parasitémie agrégée par lame.',
                  stat: 'PDF report',
                },
              ].map(({ key, icon, color, accent, title, desc, stat }) => (
                <Box
                  key={key}
                  bg="whiteAlpha.900"
                  backdropFilter="blur(10px)"
                  p={4}
                  borderRadius="xl"
                  border="1px solid"
                  borderColor="whiteAlpha.700"
                  boxShadow="md"
                >
                  <HStack align="start" justify="space-between" spacing={3} mb={3}>
                    <HStack align="start" spacing={3}>
                      <Box
                        w="44px"
                        h="44px"
                        borderRadius="xl"
                        bgGradient={color}
                        color={accent}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        flexShrink={0}
                      >
                        <Icon as={icon} boxSize={5} />
                      </Box>
                      <VStack align="start" spacing={1}>
                        <Text fontSize="sm" fontWeight="bold" color="gray.800">{title}</Text>
                        <Text fontSize="xs" color="gray.600" lineHeight="1.55">{desc}</Text>
                      </VStack>
                    </HStack>
                    <VStack spacing={1} align="end" flexShrink={0}>
                      <Badge colorScheme="purple" variant="subtle" borderRadius="full">Premium</Badge>
                      <Badge colorScheme="orange" variant="subtle" borderRadius="full">BETA</Badge>
                    </VStack>
                  </HStack>
                </Box>
              ))}
            </SimpleGrid>
            </Collapse>
          </VStack>
        </Box>
      )}

      {/* Main Content Area */}
      <Box flex={1} overflow="auto">
        {/* Recent Cases View */}
        {viewMode === 'recent-cases' && (
          <Box>
            <VStack spacing={4} align="stretch">
              <Heading size="md" color="purple.700">
                Analyses récentes
              </Heading>
              <CasesList
                specialtyId="parasitology"
                onCaseSelect={handleRecentCaseSelect}
                selectedCaseId={selectedCaseId}
              />
            </VStack>
          </Box>
        )}

        {/* New Analysis View */}
        {viewMode === 'new-analysis' && (
          <VStack spacing={6} align="stretch" maxW="1000px" mx="auto">
            <Heading size="md" color="purple.700">
              Nouvelle analyse
            </Heading>

            {/* Step 1: Slide Identifier */}
            <Box>
              <Text fontWeight="bold" mb={2} color="purple.600">
                Étape 1 — Identification de la lame
              </Text>
              <SlideIdentifierInput
                onSlideIdConfirmed={handleSlideIdConfirmed}
                currentSlideId={slideId}
              />
            </Box>

            <Divider />

            {/* Step 2: Upload Images */}
            {slideId && (
              <Box>
                <Text fontWeight="bold" mb={2} color="purple.600">
                  Étape 2 — Importer les images de frottis
                </Text>
                <MicroscopeImageUpload
                  onImagesUploaded={handleImagesUploaded}
                  uploadedImages={uploadedImageUrls}
                  analyzing={analyzing}
                  isReadOnly={false}
                />
              </Box>
            )}

            {/* Step 3: Preview & Launch Analysis */}
            {uploadedImageUrls.length > 0 && (
              <>
                <Divider />
                <Box>
                  <HStack justify="space-between" mb={4}>
                    <Text fontWeight="bold" color="purple.600">
                      Étape 3 — Vérifier et lancer
                    </Text>
                    <Button
                      colorScheme="purple"
                      size="lg"
                      onClick={handleRunAnalysis}
                      isLoading={analyzing}
                      loadingText="Analyse en cours..."
                      leftIcon={<FaMicroscope />}
                    >
                      Lancer l'analyse ALMA
                    </Button>
                  </HStack>

                  <MicroscopeEvidenceGrid
                    imagePreviews={uploadedImageUrls}
                    individualResults={individualResults}
                    showOnlyInfected={showOnlyInfected}
                    title={`${uploadedImageUrls.length} image(s) prête(s)`}
                  />
                </Box>
              </>
            )}
          </VStack>
        )}

        {/* Case Details View */}
        {viewMode === 'case-details' && (
          <VStack spacing={6} align="stretch" maxW="1200px" mx="auto">
            <HStack justify="space-between" align="flex-start" flexWrap="wrap" gap={4}>
              <Box>
                <Heading size="md" color="gray.700">
                  Résultats d'analyse
                </Heading>
                <Text fontSize="sm" color="gray.600" mt={1} fontFamily="mono">
                  {slideId}
                </Text>
              </Box>

              {/* Analysis Summary — même style que Blot */}
              {analysisResults && (
                <HStack spacing={6} bg="white" p={4} borderRadius="xl" borderWidth={1} borderColor="gray.200" shadow="md" flexWrap="wrap">
                  <Box textAlign="center">
                    <Text fontSize="xs" color="gray.600">Cellules infectées</Text>
                    <Text fontSize="2xl" fontWeight="bold" color="red.500">
                      {analysisResults.infectedRbcCount}
                    </Text>
                  </Box>
                  <Divider orientation="vertical" h="50px" />
                  <Box textAlign="center">
                    <Text fontSize="xs" color="gray.600">Cellules saines</Text>
                    <Text fontSize="2xl" fontWeight="bold" color="green.500">
                      {analysisResults.uninfectedRbcCount}
                    </Text>
                  </Box>
                  <Divider orientation="vertical" h="50px" />
                  <Box textAlign="center">
                    <Text fontSize="xs" color="gray.600">Parasitémie</Text>
                    <Text fontSize="2xl" fontWeight="bold" color="purple.600">
                      {analysisResults.parasitemiaPercent.toFixed(2)}%
                    </Text>
                  </Box>
                  {analysisTiming && !analyzing && (
                    <>
                      <Divider orientation="vertical" h="50px" />
                      <Box textAlign="center">
                        <Text fontSize="xs" color="gray.600">Durée</Text>
                        <Text fontSize="2xl" fontWeight="bold" color="gray.600">
                          {(analysisTiming.totalMs / 1000).toFixed(1)}s
                        </Text>
                        {analysisTiming.perImage.length > 1 && (
                          <Text fontSize="9px" color="gray.400">
                            avg {(analysisTiming.perImage.reduce((a, b) => a + b, 0) / analysisTiming.perImage.length / 1000).toFixed(1)}s/img
                          </Text>
                        )}
                      </Box>
                    </>
                  )}
                  {(() => {
                    const profile = individualResults.find(r => r.detectionProfile)?.detectionProfile
                    return profile ? (
                      <>
                        <Divider orientation="vertical" h="50px" />
                        <Tooltip label="Profil de détection utilisé pour adapter les paramètres au scanner">
                          <Box textAlign="center">
                            <Text fontSize="xs" color="gray.600">Profil</Text>
                            <HStack spacing={1.5} justify="center">
                              <Icon as={LuSparkles} boxSize={4} color="purple.500" />
                              <Text fontSize="md" fontWeight="bold" color="purple.600" textTransform="uppercase">
                                {profile}
                              </Text>
                            </HStack>
                          </Box>
                        </Tooltip>
                      </>
                    ) : null
                  })()}
                </HStack>
              )}
            </HStack>

            {/* Progress during analysis */}
            {analyzing && analysisTiming && (
                  <Box
                    w="100%"
                    bg="white"
                    border="1px solid"
                    borderColor="purple.100"
                    borderRadius="xl"
                    p={4}
                    boxShadow="sm"
                  >
                    <HStack justify="space-between" mb={3}>
                      <HStack spacing={2}>
                        <Box
                          w="8px"
                          h="8px"
                          borderRadius="full"
                          bg="purple.400"
                          animation="pulse 1.2s ease-in-out infinite"
                          sx={{ '@keyframes pulse': { '0%, 100%': { opacity: 1, transform: 'scale(1)' }, '50%': { opacity: 0.4, transform: 'scale(0.8)' } } }}
                        />
                        <Text fontSize="sm" color="purple.700" fontWeight="semibold">
                          Analyse IA en cours
                        </Text>
                      </HStack>
                      <HStack spacing={2}>
                        <Text fontSize="sm" fontWeight="bold" color="purple.600">
                          {analysisTiming.analyzedCount}
                        </Text>
                        <Text fontSize="sm" color="gray.400">/</Text>
                        <Text fontSize="sm" color="gray.500">
                          {analysisTiming.totalCount} images
                        </Text>
                      </HStack>
                    </HStack>

                    {/* Progress bar */}
                    <Box position="relative" mb={3}>
                      <Box w="100%" bg="purple.50" borderRadius="full" h="10px" overflow="hidden">
                        <Box
                          bg="linear-gradient(90deg, #805AD5, #B794F4)"
                          h="100%"
                          borderRadius="full"
                          w={`${(analysisTiming.analyzedCount / analysisTiming.totalCount) * 100}%`}
                          transition="width 0.4s cubic-bezier(0.4, 0, 0.2, 1)"
                          position="relative"
                          _after={{
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            bottom: 0,
                            w: '30px',
                            bg: 'linear-gradient(90deg, transparent, whiteAlpha.500)',
                            borderRadius: 'full',
                            animation: 'shimmer 1.5s ease-in-out infinite',
                          }}
                          sx={{ '@keyframes shimmer': { '0%, 100%': { opacity: 0 }, '50%': { opacity: 1 } } }}
                        />
                      </Box>
                    </Box>

                    {/* Image dots */}
                    <HStack spacing={1} flexWrap="wrap">
                      {Array.from({ length: analysisTiming.totalCount }).map((_, i) => (
                        <Box
                          key={i}
                          w="18px"
                          h="18px"
                          borderRadius="md"
                          fontSize="9px"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          fontWeight="bold"
                          color="white"
                          transition="all 0.3s ease"
                          bg={
                            i < analysisTiming.analyzedCount
                              ? 'purple.500'
                              : i === analysisTiming.analyzedCount
                              ? 'purple.300'
                              : 'gray.200'
                          }
                          transform={i === analysisTiming.analyzedCount ? 'scale(1.15)' : 'scale(1)'}
                        >
                          {i < analysisTiming.analyzedCount ? '✓' : i + 1}
                        </Box>
                      ))}
                    </HStack>

                    {analysisTiming.perImage.length > 0 && (
                      <Text fontSize="xs" color="gray.400" mt={2}>
                        Dernière image : {(analysisTiming.perImage[analysisTiming.perImage.length - 1] / 1000).toFixed(1)}s
                        {analysisTiming.totalMs > 0 && ` · Temps écoulé : ${(analysisTiming.totalMs / 1000).toFixed(1)}s`}
                      </Text>
                    )}
                  </Box>
                )}

                {/* Debug Images Button - Shown when debug images are available */}
                {debugImages && Object.keys(debugImages).length > 0 && (
                  <Button
                    size="md"
                    colorScheme="orange"
                    variant="outline"
                    onClick={onDebugModalOpen}
                    leftIcon={<Icon viewBox="0 0 24 24"><path fill="currentColor" d="M12 9a3 3 0 0 0-3 3a3 3 0 0 0 3 3a3 3 0 0 0 3-3a3 3 0 0 0-3-3m0 8a5 5 0 0 1-5-5a5 5 0 0 1 5-5a5 5 0 0 1 5 5a5 5 0 0 1-5 5m0-12.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5Z"/></Icon>}
                  >
                    Voir les images de debug ({Object.keys(debugImages).length} étapes)
                  </Button>
                )}

                {/* Action Buttons */}
                <HStack spacing={2}>
                  {!showAddImagesForm && (
                    <Button
                      leftIcon={<MdAdd />}
                      onClick={() => setShowAddImagesForm(true)}
                      colorScheme="blue"
                      variant="outline"
                      size="md"
                      _hover={{ bg: 'blue.50' }}
                    >
                      Ajouter des images
                    </Button>
                  )}
                  <Checkbox
                    isChecked={debugMode}
                    onChange={(e) => setDebugMode(e.target.checked)}
                    colorScheme="orange"
                    size="sm"
                  >
                    Mode Debug (afficher les étapes intermédiaires)
                  </Checkbox>
                  {newlyAddedFiles.length === 0 ? (
                    <Button
                      leftIcon={<MdRefresh />}
                      onClick={handleRerunExistingOnly}
                      isLoading={analyzing}
                      loadingText="Ré-analyse en cours..."
                      colorScheme="purple"
                      variant="outline"
                      size="md"
                      _hover={{ bg: 'purple.50' }}
                    >
                      Relancer l'analyse
                    </Button>
                  ) : (
                    <>
                      <Button
                        leftIcon={<FaMicroscope />}
                        onClick={handleAnalyzeNewOnly}
                        isLoading={analyzing}
                        loadingText="Analyse en cours..."
                        colorScheme="green"
                        size="md"
                        _hover={{ bg: 'green.50' }}
                      >
                        Analyser les nouvelles ({newlyAddedFiles.length})
                      </Button>
                      <Button
                        leftIcon={<MdRefresh />}
                        onClick={handleRerunAllAnalysis}
                        isLoading={analyzing}
                        loadingText="Ré-analyse en cours..."
                        colorScheme="purple"
                        variant="outline"
                        size="md"
                        _hover={{ bg: 'purple.50' }}
                      >
                        Tout ré-analyser
                      </Button>
                    </>
                  )}
                  <ParasitologyExportButton
                    slideId={slideId!}
                    analysisResults={analysisResults}
                    individualResults={individualResults}
                    imageUrls={uploadedImageUrls}
                    analysisTiming={analysisTiming}
                    isDisabled={analyzing || !hasResults}
                  />
            </HStack>

            {/* ── Premium upsell ALMA ───────────────────────────────────── */}
            {isPremiumLocked && (
              <Box
                position="relative"
                overflow="hidden"
                bg="linear-gradient(145deg, #faf5ff 0%, #fff 35%, #f0f9ff 100%)"
                border="1px solid"
                borderColor="purple.200"
                borderRadius="xl"
                p={{ base: 3, md: 4 }}
                boxShadow="sm"
              >
                <VStack align="stretch" spacing={3} position="relative" zIndex={1}>
                  <HStack justify="space-between" align="center" spacing={4}>
                    <HStack spacing={3} align="center" flex={1}>
                      <Box w="36px" h="36px" borderRadius="lg" bg="linear-gradient(135deg, #805ad5 0%, #3182ce 100%)" display="flex" alignItems="center" justifyContent="center" flexShrink={0}>
                        <Icon as={LuSparkles} color="white" boxSize={4} />
                      </Box>
                      <VStack align="start" spacing={0}>
                        <HStack spacing={2} mb={0.5}>
                          <Badge bg="purple.500" color="white" px={2} py={0.5} borderRadius="full" fontSize="2xs">BETA</Badge>
                          <PremiumBadge />
                        </HStack>
                        <Text fontWeight="bold" fontSize="sm" color="gray.900">
                          Détection IA des globules parasités
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                          ALMA détecte automatiquement les hématies infectées — 80% de temps gagné
                        </Text>
                      </VStack>
                    </HStack>
                    <Button
                      size="md"
                      bgGradient="linear(to-r, purple.500, blue.400)"
                      color="white"
                      rightIcon={<Icon as={LuArrowRight} />}
                      borderRadius="xl"
                      px={6}
                      py={5}
                      fontWeight="bold"
                      fontSize="sm"
                      flexShrink={0}
                      boxShadow="0 4px 20px rgba(128,90,213,0.4)"
                      _hover={{ bgGradient: 'linear(to-r, purple.600, blue.500)', transform: 'translateY(-2px)', boxShadow: '0 8px 28px rgba(128,90,213,0.5)' }}
                      transition="all 0.2s"
                      onClick={() => navigate('/change-plan')}
                    >
                      Passer au Premium
                    </Button>
                  </HStack>

                  <Box as="button" onClick={() => setShowFeatureCards(v => !v)} w="100%" py={1} display="flex" alignItems="center" justifyContent="center" gap={2} color="purple.500" fontSize="xs" fontWeight="semibold" _hover={{ color: 'purple.700' }} transition="color 0.2s">
                    <Text>{showFeatureCards ? 'Masquer les détails' : 'Voir ce qui est inclus'}</Text>
                    <Box transform={showFeatureCards ? 'rotate(180deg)' : 'rotate(0deg)'} transition="transform 0.25s ease" fontSize="14px">▾</Box>
                  </Box>

                  <Collapse in={showFeatureCards} animateOpacity>
                    <SimpleGrid columns={{ base: 1, xl: 3 }} spacing={3}>
                      {[
                        { key: 'detection', icon: LuSparkles, color: 'linear(to-br, purple.100, pink.100)', accent: 'purple.500', title: 'Détection automatique ALMA', desc: 'Classification automatique infecté / sain. 80% de temps gagné vs comptage manuel.' },
                        { key: 'accuracy', icon: LuActivity, color: 'linear(to-br, blue.100, cyan.100)', accent: 'blue.500', title: 'Précision clinique', desc: 'Accuracy 99.7% · Sensibilité 77.9% · Spécificité 99.8% — évaluation patient-level.' },
                        { key: 'export', icon: LuScanLine, color: 'linear(to-br, teal.100, green.100)', accent: 'teal.500', title: 'Export PDF enrichi', desc: 'Images annotées, overlay de détection, parasitémie agrégée par lame.' },
                      ].map(({ key, icon, color, accent, title, desc }) => (
                        <Box key={key} bg="whiteAlpha.900" p={3} borderRadius="xl" border="1px solid" borderColor="whiteAlpha.700" boxShadow="sm">
                          <HStack align="start" spacing={3}>
                            <Box w="36px" h="36px" borderRadius="lg" bgGradient={color} color={accent} display="flex" alignItems="center" justifyContent="center" flexShrink={0}>
                              <Icon as={icon} boxSize={4} />
                            </Box>
                            <VStack align="start" spacing={0.5} flex={1}>
                              <Text fontSize="xs" fontWeight="bold" color="gray.800">{title}</Text>
                              <Text fontSize="xs" color="gray.500" lineHeight="1.45">{desc}</Text>
                            </VStack>
                          </HStack>
                        </Box>
                      ))}
                    </SimpleGrid>
                  </Collapse>
                </VStack>
              </Box>
            )}

            {/* Add Images Form */}
            {showAddImagesForm && (
              <Box bg="blue.50" p={4} borderRadius="xl" borderWidth={1} borderColor="blue.200">
                <HStack justify="space-between" mb={3}>
                  <Text fontWeight="bold" color="blue.700">
                    Add New Images to Analysis
                  </Text>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setShowAddImagesForm(false)
                      setNewlyAddedFiles([])
                      setNewlyAddedImageUrls([])
                    }}
                  >
                    Cancel
                  </Button>
                </HStack>
                <MicroscopeImageUpload
                  onImagesUploaded={handleNewImagesAdded}
                  uploadedImages={newlyAddedImageUrls}
                  analyzing={false}
                  isReadOnly={false}
                />
              </Box>
            )}

            <MicroscopeEvidenceGrid
              imagePreviews={uploadedImageUrls}
              individualResults={individualResults}
              showOnlyInfected={showOnlyInfected}
              title="Analysis Results"
              analyzingImageIndex={analyzing ? analyzingImageIndex : -1}
              onBoxLabelChange={handleBoxLabelChange}
            />
          </VStack>
        )}
      </Box>

      {/* Reanalysis Image Selection Modal */}
      <Modal isOpen={isReanalysisModalOpen} onClose={onReanalysisModalClose} size="6xl">
        <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
        <ModalContent maxH="90vh" bg="gray.50">
          <ModalHeader bg="white" borderBottom="1px" borderColor="gray.200" py={4}>
            <VStack align="start" spacing={1}>
              <HStack spacing={2}>
                <Icon as={MdRefresh} boxSize={6} color="purple.500" />
                <Text fontSize="xl" fontWeight="bold" color="gray.800">
                  Sélection pour Réanalyse
                </Text>
              </HStack>
              <Text fontSize="sm" fontWeight="normal" color="gray.600">
                Choisissez les images à analyser à nouveau
              </Text>
            </VStack>
          </ModalHeader>
          <ModalCloseButton top={4} />

          <ModalBody py={6} overflowY="auto">
            <VStack align="stretch" spacing={5}>
              {/* Stats and Actions Bar */}
              <Card bg="purple.50" borderColor="purple.200" borderWidth={1}>
                <CardBody py={3}>
                  <HStack justify="space-between" flexWrap="wrap">
                    <HStack spacing={3}>
                      <Badge colorScheme="purple" fontSize="md" px={3} py={1} borderRadius="md">
                        {selectedImageIndices.length} / {uploadedImageUrls.length}
                      </Badge>
                      <Text fontSize="sm" color="purple.800" fontWeight="medium">
                        {selectedImageIndices.length === 0
                          ? 'Aucune image sélectionnée'
                          : selectedImageIndices.length === 1
                          ? '1 image sélectionnée'
                          : `${selectedImageIndices.length} images sélectionnées`}
                      </Text>
                    </HStack>
                    <HStack spacing={2}>
                      <Button
                        size="sm"
                        variant="outline"
                        colorScheme="purple"
                        onClick={selectAllImages}
                        leftIcon={<Icon as={MdCheckCircle} />}
                      >
                        Tout sélectionner
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        colorScheme="gray"
                        onClick={deselectAllImages}
                      >
                        Tout désélectionner
                      </Button>
                    </HStack>
                  </HStack>
                </CardBody>
              </Card>

              {/* Image Grid */}
              <SimpleGrid columns={{ base: 2, md: 3, lg: 4 }} spacing={4}>
                {uploadedImageUrls.map((url, index) => {
                  const isSelected = selectedImageIndices.includes(index)
                  return (
                    <Card
                      key={index}
                      cursor="pointer"
                      onClick={() => toggleImageSelection(index)}
                      transition="all 0.2s"
                      borderWidth={2}
                      borderColor={isSelected ? 'purple.500' : 'gray.200'}
                      bg={isSelected ? 'purple.50' : 'white'}
                      _hover={{
                        borderColor: isSelected ? 'purple.600' : 'purple.300',
                        transform: 'translateY(-2px)',
                        shadow: 'lg',
                      }}
                      position="relative"
                      overflow="hidden"
                    >
                      {/* Selection Badge */}
                      {isSelected && (
                        <Badge
                          position="absolute"
                          top={2}
                          right={2}
                          colorScheme="purple"
                          borderRadius="full"
                          px={2}
                          py={1}
                          zIndex={1}
                          fontSize="xs"
                        >
                          ✓ Sélectionné
                        </Badge>
                      )}

                      <CardBody p={0}>
                        <Box position="relative" overflow="hidden">
                          <Image
                            src={normalizeImageUrl(url)}
                            alt={`Image ${index + 1}`}
                            objectFit="cover"
                            w="100%"
                            h="180px"
                            opacity={isSelected ? 0.9 : 1}
                            transition="opacity 0.2s"
                          />
                        </Box>

                        <VStack align="stretch" spacing={2} p={3} bg={isSelected ? 'purple.100' : 'gray.50'}>
                          <HStack justify="space-between">
                            <HStack spacing={2}>
                              <Icon as={MdImage} color={isSelected ? 'purple.600' : 'gray.500'} />
                              <Text fontSize="sm" fontWeight="bold" color={isSelected ? 'purple.800' : 'gray.700'}>
                                Image {index + 1}
                              </Text>
                            </HStack>
                            <Checkbox
                              isChecked={isSelected}
                              onChange={() => toggleImageSelection(index)}
                              colorScheme="purple"
                              onClick={(e) => e.stopPropagation()}
                              size="lg"
                            />
                          </HStack>

                          {individualResults[index] && (
                            <HStack spacing={2} justify="space-between" pt={1}>
                              <HStack spacing={1}>
                                <Badge colorScheme="red" fontSize="xs" variant="subtle">
                                  {individualResults[index].infectedRbcCount} 🦠
                                </Badge>
                              </HStack>
                              <HStack spacing={1}>
                                <Badge colorScheme="green" fontSize="xs" variant="subtle">
                                  {individualResults[index].uninfectedRbcCount} ✓
                                </Badge>
                              </HStack>
                            </HStack>
                          )}
                        </VStack>
                      </CardBody>
                    </Card>
                  )
                })}
              </SimpleGrid>
            </VStack>
          </ModalBody>

          <ModalFooter bg="white" borderTop="1px" borderColor="gray.200" py={4}>
            <HStack spacing={3} w="full" justify="flex-end">
              <Button
                variant="ghost"
                onClick={onReanalysisModalClose}
                size="md"
              >
                Annuler
              </Button>
              <Button
                colorScheme="purple"
                onClick={performReanalysis}
                isDisabled={selectedImageIndices.length === 0}
                size="md"
                leftIcon={<Icon as={MdRefresh} />}
              >
                Réanalyser {selectedImageIndices.length} image{selectedImageIndices.length !== 1 ? 's' : ''}
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Debug Images Modal */}
      <Modal isOpen={isDebugModalOpen} onClose={onDebugModalClose} size="6xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Images de Debug - Étapes Intermédiaires du Traitement</ModalHeader>
          <ModalCloseButton />
          <ModalBody maxH="80vh" overflowY="auto">
            {debugImages && (
              <SimpleGrid columns={[1, 2, 3]} spacing={4}>
                {Object.entries(debugImages).map(([key, base64Image]) => {
                  const stepName = key.replace(/_/g, ' ').replace(/^\d+\s*/, '')
                  return (
                    <VStack key={key} spacing={2} align="stretch">
                      <Text fontSize="sm" fontWeight="bold" textAlign="center" color="orange.600">
                        {stepName}
                      </Text>
                      <Box borderWidth={2} borderColor="orange.300" borderRadius="md" overflow="hidden">
                        <Image
                          src={`data:image/png;base64,${base64Image}`}
                          alt={stepName}
                          objectFit="contain"
                          w="100%"
                          h="300px"
                        />
                      </Box>
                    </VStack>
                  )
                })}
              </SimpleGrid>
            )}
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="orange" onClick={onDebugModalClose}>
              Fermer
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  )
}
